import React, {useState} from "react";
import {Card, Table, Tooltip, Row, Alert, Tag, Button, message} from "antd";
import "./CodPastPeriodsPage.css";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {CodPastPeriodsPageFilters} from "./CodPastPeriodsPageFilters";
import moment from "moment";
import {b64DecodeToUrl, isConstantExist} from "../../../utils/helpers";
import {
    codPaymentReferenceCodeStatuses,
    codPaymentStatuses,
    codPaymentTypes
} from "../../../constants/constants";
import {useTranslation} from "react-i18next";
import {isNumber} from "lodash";
import {InfoCircleOutlined, InfoOutlined} from "@ant-design/icons";
import {formatMoney2} from "../../../utils/moneyHelper";
import {TableWrapper} from "../../_defaultComponents/TableWrapper/TableWrapper";
const PAGE_SIZE = 30;

export const CodPastPeriodsPage = ({isTabOpened = true, isFromCustomerTheme}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [fromCustomerTheme] = useState(isFromCustomerTheme);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [recall, setRecall] = useState(false);
    const [loadingExcelData, setLoadingExcelData] = useState(false);

    const [loading, codPaymentReferenceCodesResult] = useFetch({
        fetchFn: stores.codPaymentStore.getPastPeriods.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: isTabOpened,
        waitForResponse: true
    });

    const renderCodPaymentsTable = record => {
        const codPaymentsTableColumns = [
            {
                title: "Sipariş No",
                dataIndex: ["delivery", "orderNumber"],
                key: "orderNumber",
                width: 140,
                render: (text, row) => (
                    <Link
                        data-testid="orderNumber"
                        target="_blank"
                        to={{
                            pathname: fromCustomerTheme
                                ? "/musteri/siparislerim/" + row?.deliveryInfo?._id
                                : "/orders/" + row?.deliveryInfo?._id
                        }}
                    >
                        {text}
                    </Link>
                )
            },
            {
                title: "Alıcı Adı",
                dataIndex: ["deliveryInfo", "order", "receiverName"],
                key: "receiverName",
                width: 100,
                /* ellipsis: {
                    showTitle: false
                }, */
                render: text => <Tooltip title={text}>{text}</Tooltip>
            },
            {
                title: (
                    <>
                        Sipariş
                        <br />
                        Tarihi
                    </>
                ),
                dataIndex: ["deliveryInfo"],
                key: "orderDateOnly",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: deliveryInfo => {
                    const orderDate = deliveryInfo?.order?.mergedAt || deliveryInfo?.createdAt;

                    const orderDateOnly = orderDate ? moment(orderDate).format("DD.MM.YYYY") : "-";
                    return <Tooltip title={orderDateOnly}>{orderDateOnly}</Tooltip>;
                }
            },
            {
                title: "Sipariş Saati",
                dataIndex: ["deliveryInfo"],
                key: "orderTimeOnly",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: deliveryInfo => {
                    const orderDate = deliveryInfo?.order?.mergedAt || deliveryInfo?.createdAt;

                    const orderTimeOnly = orderDate ? moment(orderDate).format("HH:mm") : "-";

                    return <Tooltip title={orderTimeOnly}>{orderTimeOnly}</Tooltip>;
                }
            },
            {
                title: "Kurye",
                dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
                key: "courierName",
                width: 150,
                render: (text, row) => (
                    <>
                        {fromCustomerTheme ? (
                            <>{text}</>
                        ) : (
                            <Link
                                data-testid="orderNumber"
                                target="_blank"
                                to={{
                                    pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                                }}
                            >
                                {text}
                            </Link>
                        )}
                    </>
                )
            },
            {
                title: (
                    <>
                        Ödeme Tipi
                        <br />
                        (ilk)
                    </>
                ),
                dataIndex: ["payment", "paymentType"],
                key: "firstPaymentType",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => {
                    const paymentType = isConstantExist(codPaymentTypes, text)
                        ? t(`paymentTypes.${text}`, {
                              ns: "codPayment"
                          })
                        : text;
                    return <Tooltip title={paymentType}>{paymentType}</Tooltip>;
                }
            },
            {
                title: (
                    <>
                        Sipariş
                        <br />
                        Tutarı
                    </>
                ),
                dataIndex: ["deliveryInfo", "order", "price"],
                key: "totalOrderAmount",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => (
                    <Tooltip title={formatMoney2({amount: text})}>
                        {formatMoney2({amount: text})}
                    </Tooltip>
                )
            },
            {
                title: (
                    <>
                        Hesaba
                        <br />
                        Yatırılacak
                        <br />
                        Tutar
                    </>
                ),
                dataIndex: ["payment", "payableAmount"],
                key: "payableAmount",
                width: "100px",
                ellipsis: {
                    showTitle: false
                },
                render: text => (
                    <Tooltip title={formatMoney2({amount: text})}>
                        {formatMoney2({amount: text})}
                    </Tooltip>
                )
            },
            {
                title: (
                    <>
                        Ödeme Tipi
                        <br />
                        (işlem)
                    </>
                ),
                dataIndex: ["transactions"],
                key: "processedPaymentType",
                width: 120,
                render: (transactions, record) => {
                    const uniqePaymentTypes = [
                        ...new Set(transactions?.map(payment => payment?.paymentType) || [])
                    ];

                    const paymentTypes = uniqePaymentTypes?.map(paymentType => {
                        return isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType;
                    });

                    return (
                        <Tooltip title={paymentTypes.join(", ")}>
                            {paymentTypes.map(pT => (
                                <React.Fragment key={pT}>
                                    {pT}
                                    <br />
                                </React.Fragment>
                            ))}
                        </Tooltip>
                    );
                }
            },
            {
                title: "Durum",
                dataIndex: ["status"],
                key: "status",
                width: 200,
                ellipsis: {
                    showTitle: false
                },
                hideForCustomerTheme: true,
                render: text => {
                    const status = isConstantExist(codPaymentStatuses, text)
                        ? t(`codPaymentStatuses.${text}`, {
                              ns: "codPayment"
                          })
                        : text;
                    return <Tooltip title={status}>{status}</Tooltip>;
                }
            }
        ];

        return (
            <Table
                bordered
                columns={
                    codPaymentsTableColumns?.filter(column =>
                        fromCustomerTheme ? !column?.hideForCustomerTheme : true
                    ) || []
                }
                dataSource={record?.codPayments || []}
                expandable={{
                    expandedRowRender: renderTransactionsTable,
                    defaultExpandedRowKeys: ["0"]
                }}
                scroll={{x: 1200}}
                pagination={true}
                rowKey="_id"
                size="small"
            />
        );
    };

    const renderTransactionsTable = codPayment => {
        const transactionsTableColumns = [
            {
                title: "Ödeme Id(trx)",
                dataIndex: ["transactionId"],
                key: "transactionId",
                width: "180px",
                ellipsis: {
                    showTitle: false
                },
                hideForCustomerTheme: true,
                render: text => <Tooltip title={text}>{text}</Tooltip>
            },
            {
                title: "Ödeme Tarihi",
                dataIndex: ["createdAt"],
                key: "paymentCreatedAt",
                width: 100,
                render: text => text && moment(text).format("DD.MM.YYYY")
            },
            {
                title: "Ödeme Saati",
                dataIndex: ["createdAt"],
                key: "paymentCreatedAt",
                width: 100,
                render: text => text && moment(text).format("HH:mm:ss")
            },
            {
                title: "Banka Transfer Tarihi",
                dataIndex: ["invoice", "reconciliationDate"],
                key: "reconciliationDate",
                width: 100,
                hideForCustomerTheme: true,
                render: (text, row) =>
                    codPayment?.status === "BANK_VERIFIED" && !["cash"].includes(row?.paymentType)
                        ? text && moment(text).format("DD.MM.YYYY")
                        : "-"
            },
            {
                title: "Ödeme Tipi",
                dataIndex: ["paymentType"],
                key: "processedPaymentType",
                width: 120,
                render: paymentType =>
                    `${
                        isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType
                    }`
            },
            {
                title: "Komisyon Oranı",
                dataIndex: ["invoice", "commissionRate"],
                key: "commissionRate",
                width: 150,
                render: (text, row) => (
                    <>{row.paymentType === "cash" ? "-" : text ? `${text}%` : "0%"}</>
                )
            },
            {
                title: "Ödeme Tutarı",
                dataIndex: ["transactionAmount"],
                key: "amount",
                width: 100,
                render: text => formatMoney2({amount: text})
            },
            {
                title: "Hesaba Yatırılacak Tutar",
                dataIndex: ["invoice", "payableAmount"],
                key: "payableAmount",
                width: 100,
                render: text => formatMoney2({amount: text})
            },
            {
                title: "Durum",
                dataIndex: ["providerPaymentStatus", "status"],
                key: "status",
                width: 100,
                hideForCustomerTheme: true,
                render: text =>
                    text &&
                    `${t(`codPaymentTransactionProviderPaymentStatuses.${text}`, {
                        ns: "codPayment"
                    })}`
            }
        ];

        return (
            <Table
                className="cod-transactions-table"
                bordered
                columns={
                    transactionsTableColumns?.filter(column =>
                        fromCustomerTheme ? !column?.hideForCustomerTheme : true
                    ) || []
                }
                dataSource={codPayment?.transactions || []}
                pagination={false}
                rowKey="_id"
                size="small"
            />
        );
    };

    const getExcelExport = async () => {
        const messageKey = "all-wallet-couriers-excel";
        message.loading({content: "Excel hazırlanıyor...", key: messageKey});
        setLoadingExcelData(true);
        try {
            const response = await stores.codPaymentStore.getPastPeriods({
                ...filters,
                excel: true
            });

            const base64Data = response?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "kapida-odeme-gecmis-dönemler.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success({
                content: "Excel dosyası başarıyla indirildi.",
                key: messageKey,
                duration: 4
            });
        } catch (error) {
            console.log("error", error);
            message.error({
                content: "Excel dosyası indirilirken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingExcelData(false);
        }
    };

    const referenceCodeTableColumns = [
        {
            title: "Referans Fatura No",
            dataIndex: "referenceCode",
            key: "referenceCode",
            render: (text, record) => {
                const dueDate = record?.dueDate ? moment(record?.dueDate) : moment();

                const now = moment();

                return (
                    <>
                        {fromCustomerTheme ? (
                            text && (
                                <Tooltip
                                    title={
                                        dueDate < now
                                            ? text
                                            : `${dueDate?.format("DD.MM.YYYY") ||
                                                  ""} tarihinde görünür olacaktır`
                                    }
                                >
                                    <Alert
                                        message={dueDate < now ? text : "Bekleniyor..."}
                                        type={dueDate < now ? "error" : "info"}
                                        style={{width: "max-content", fontWeight: "bolder"}}
                                    />
                                </Tooltip>
                            )
                        ) : (
                            <Row align="middle">
                                <Alert
                                    message={
                                        <Tooltip
                                            title={
                                                !Boolean(dueDate < now)
                                                    ? `Müşteriye, ${dueDate?.format("DD.MM.YYYY") ||
                                                          ""} tarihinden itibaren gösterilecektir.`
                                                    : ""
                                            }
                                        >
                                            <Row align="middle">
                                                {text}
                                                {!Boolean(dueDate < now) && (
                                                    <InfoCircleOutlined
                                                        style={{marginLeft: "4px"}}
                                                    />
                                                )}
                                            </Row>
                                        </Tooltip>
                                    }
                                    type={dueDate < now ? "error" : "info"}
                                    style={{width: "max-content", fontWeight: "bolder"}}
                                />
                            </Row>
                        )}
                    </>
                );
            }
        },
        {
            title: "Toplam Fatura Tutarı",
            dataIndex: "receivedAmount",
            key: "receivedAmount",
            render: text =>
                text && (
                    <Alert
                        message={formatMoney2({amount: text})}
                        type="error"
                        style={{width: "max-content", fontWeight: "bolder"}}
                    />
                )
        },
        {
            title: "Toplam İşlem Adedi",
            dataIndex: "totalCodPayments",
            key: "totalCodPayments",
            render: totalCodPayments => totalCodPayments || 0
        },
        {
            title: "Toplam Hesaba Yatırılacak Tutar",
            dataIndex: "payableAmount",
            key: "payableAmount",
            render: text => formatMoney2({amount: text})
        },
        {
            title: "Firma",
            dataIndex: ["company", "companyName"],
            key: "companyName",
            hideForCustomerTheme: true,
            render: (text, record) => (
                <Link to={`/companies/${record?.company?._id}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "Alt Firma",
            dataIndex: ["subCompany", "name"],
            key: "subcompany",
            hideForCustomerTheme: true,
            render: (text, record) => (
                <Link to={`/companies/sub-company/${record?.subCompany?._id}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            render: text => (
                <Tag
                    color={
                        codPaymentReferenceCodeStatuses?.find(status => status.value === text)
                            ?.color
                    }
                >
                    <b>
                        {text
                            ? t(`codPaymentReferenceCodeStatuses.${text}`, {
                                  ns: "codPayment"
                              })
                            : "-"}
                    </b>
                </Tag>
            )
        }
    ];

    return (
        <Card>
            {fromCustomerTheme ? (
                <Alert
                    message='Dikkat: E-fatura keserken, "Sipariş No" alanına "Referans No" bilgisini yazınız.'
                    type="error"
                    style={{marginBottom: 16, fontWeight: "bolder"}}
                />
            ) : (
                <></>
            )}
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <CodPastPeriodsPageFilters
                    filters={filters}
                    setFilters={setFilters}
                    isFromCustomerTheme={fromCustomerTheme}
                    loading={loading}
                />
            </Row>
            <Row gutter={[16, 16]} style={{marginBottom: 8}} justify="end">
                <Button type="primary" onClick={getExcelExport} loading={loadingExcelData}>
                    {loadingExcelData ? "Excel Hazırlanıyor..." : "Excel İndir"}
                </Button>
            </Row>
            <div style={{marginTop: "20px"}}>
                <TableWrapper
                    loading={loading}
                    columns={referenceCodeTableColumns.filter(column =>
                        fromCustomerTheme ? !column?.hideForCustomerTheme : true
                    )}
                    dataSource={codPaymentReferenceCodesResult?.rows || []}
                    rowKey="_id"
                    expandable={{
                        expandedRowRender: renderCodPaymentsTable,
                        defaultExpandedRowKeys: ["0"]
                    }}
                    scroll={{x: 1200}}
                    pagination={{
                        total: codPaymentReferenceCodesResult?.dataCount || 0,
                        current: page,
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        pageSize: PAGE_SIZE,
                        onChange: page => {
                            setPage(page);
                        }
                    }}
                />
            </div>
        </Card>
    );
};
